import React, { useState, useEffect } from 'react';
import './GestionNominaScreen.css';
import { IoCashOutline, IoArrowBack, IoCalculatorOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { collection, getDocs, doc, setDoc, getDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { calcularNominaEmpleado } from '../utils/MotorNomina';
import { getApprovalRules, validateInvoiceRules } from '../utils/approvalService';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function GestionNominaScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userData } = useAuth();
    
    // Contexto SaaS
    const isSaaS = location.pathname.includes('/superuser/hr');
    const basePath = isSaaS ? '/superuser/hr' : '';
    const activeCondominioId = isSaaS ? 'SAAS' : userData?.condominioId;

    const [empleados, setEmpleados] = useState([]);
    const [isReadOnly, setIsReadOnly] = useState(false);

    // Form fields
    const [selectedEmpleadoId, setSelectedEmpleadoId] = useState('');
    const [fechaInicioPeriodo, setFechaInicioPeriodo] = useState('');
    const [fechaFinPeriodo, setFechaFinPeriodo] = useState('');
    const [incidenciasPendientes, setIncidenciasPendientes] = useState([]);

    useEffect(() => {
        if (location.state?.preselectEmpleadoId) {
            setSelectedEmpleadoId(location.state.preselectEmpleadoId);
        }

        if (location.state?.nominaReferenceId) {
            const fetchNomina = async () => {
                try {
                    const docSnap = await getDoc(doc(db, 'nominas_periodos', location.state.nominaReferenceId));
                    if (docSnap.exists()) {
                        const data = docSnap.data();

                        setCalculoActual({
                            empleado: {
                                id: data.empleadoId,
                                nombre: data.empleadoNombre, // Map as full name since we joined it
                                apellidos: '',
                                salarioBruto: data.salarioBruto || (data.salarioDiario * 30) // Fallback for old receipts
                            },
                            periodo: {
                                diasBase: data.diasPeriodo || 15,
                                faltas: data.faltas || 0,
                                incapacidades: data.incapacidades || 0
                            },
                            diasTrabajados: data.diasTrabajados !== undefined ? data.diasTrabajados : ((data.diasPeriodo || 15) - (data.faltas || 0) - (data.incapacidades || 0)),
                            sbc: data.sbc,
                            salarioDiario: data.salarioDiario,
                            percepcionesDesglose: data.percepcionesDesglose || { sueldo: data.totalPercepciones },
                            totalPercepciones: data.totalPercepciones,
                            deducciones: data.deducciones,
                            totalDeduccionesVal: data.totalDeduccionesVal || null,
                            netoPagar: data.netoPagar,
                            transferenciaBancaria: data.transferenciaBancaria || (data.netoPagar - (data.percepcionesDesglose?.valesDespensa || 0))
                        });
                        setSelectedEmpleadoId(data.empleadoId);
                        setDiasPeriodo(data.diasPeriodo);
                        setFaltas(data.faltas);
                        setIncapacidades(data.incapacidades);
                        setFechaInicioPeriodo(data.fechaInicioPeriodo || '');
                        setFechaFinPeriodo(data.fechaFinPeriodo || '');
                        setIsReadOnly(true);
                    }
                } catch (error) {
                    console.error("Error fetching nomina: ", error);
                }
            };
            fetchNomina();
        }
    }, [location.state]);
    const [diasPeriodo, setDiasPeriodo] = useState(15);
    const [faltas, setFaltas] = useState(0);
    const [incapacidades, setIncapacidades] = useState(0);

    const [calculoActual, setCalculoActual] = useState(null);
    const [empleadoPrestaciones, setEmpleadoPrestaciones] = useState(null);
    const [loadingPrestaciones, setLoadingPrestaciones] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchEmpleados = async () => {
            if (!currentUser) return;
            try {
                const snap = await getDocs(collection(db, "empleados"));
                const empData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Limitar al contexto activo (SaaS o Condominio)
                const filtered = empData.filter(e => e.condominioId === activeCondominioId && e.estatus !== 'Inactivo');
                setEmpleados(filtered);
            } catch (err) {
                console.error("Error fetching empleados:", err);
            }
        };
        fetchEmpleados();
    }, [currentUser, userData]);

    // Cuando se selecciona un Empleado, cargar sus prestaciones (INFONAVIT, Préstamos) Y sus Incidencias Pendientes
    useEffect(() => {
        const loadPrestaciones = async () => {
            if (!selectedEmpleadoId || isReadOnly) {
                if (!isReadOnly) {
                    setEmpleadoPrestaciones(null);
                    setCalculoActual(null);
                    setIncidenciasPendientes([]);
                }
                return;
            }
            setLoadingPrestaciones(true);
            try {
                // 1. Prestaciones
                const prestRef = collection(db, `empleados/${selectedEmpleadoId}/prestaciones`);
                const prestSnap = await getDocs(prestRef);
                if (!prestSnap.empty) {
                    setEmpleadoPrestaciones(prestSnap.docs[0].data());
                } else {
                    setEmpleadoPrestaciones(null);
                }

                // 2. Incidencias
                const incRef = collection(db, `empleados/${selectedEmpleadoId}/incidencias`);
                const qInc = query(incRef, where('estatus', '==', 'Pendiente Aplicación'));
                const incSnap = await getDocs(qInc);
                const pendientes = incSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setIncidenciasPendientes(pendientes);

                // Auto-fill faltas e incapacidades si aplican
                let cFaltas = 0;
                let cIncap = 0;
                pendientes.forEach(inc => {
                    if (inc.tipo === 'Falta Injustificada') cFaltas += Number(inc.cantidad) || 0;
                    if (inc.tipo === 'Incapacidad IMSS') cIncap += Number(inc.cantidad) || 0;
                });

                // Si el usuario ya tenia algo escrito manualmente no lo sobreescribimos a menos que haya incidencias fuertes
                if (cFaltas > 0) setFaltas(cFaltas);
                if (cIncap > 0) setIncapacidades(cIncap);

            } catch (error) {
                console.error("Error loading prestaciones o incidencias:", error);
            } finally {
                setLoadingPrestaciones(false);
            }
        };
        loadPrestaciones();
    }, [selectedEmpleadoId, isReadOnly]);

    const handleCalcular = () => {
        const empleado = empleados.find(e => e.id === selectedEmpleadoId);
        if (!empleado) return;

        // Inyectar prestaciones actuales al objeto empleado para que el MotorNomina las lea
        if (empleadoPrestaciones) {
            empleado.prestaciones = empleadoPrestaciones;
        }

        const periodoData = {
            diasBase: Number(diasPeriodo),
            faltas: Number(faltas),
            incapacidades: Number(incapacidades),
            descuentoInfonavit: empleadoPrestaciones?.descuentoInfonavit || 0,
            descuentoPrestamo: empleadoPrestaciones?.descuentoPrestamo || 0
        };

        const resultado = calcularNominaEmpleado(empleado, periodoData);
        setCalculoActual({
            empleado,
            periodo: periodoData,
            ...resultado
        });
    };

    const formatter = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
    });

    const handleGuardarRecibo = async () => {
        if (!calculoActual || !currentUser) return;

        const confirmGuardar = window.confirm("¿Estás seguro de generar y congelar este recibo? Pasará al comité para su aprobación antes de poder timbrarse.");
        if (!confirmGuardar) return;

        setIsSaving(true);
        try {
            const reciboId = `${calculoActual.empleado.id}_${new Date().getTime()}`;

            // 1. Guardar Histórico de Recibo de Nómina
            const nominaRef = doc(db, 'nominas_periodos', reciboId);
            const nominaPayload = {
                empleadoId: calculoActual.empleado.id,
                empleadoNombre: `${calculoActual.empleado.nombre} ${calculoActual.empleado.apellidos}`,
                condominioId: calculoActual.empleado.condominioId,
                fechaGeneracion: new Date(),
                fechaInicioPeriodo: fechaInicioPeriodo || null,
                fechaFinPeriodo: fechaFinPeriodo || null,
                diasPeriodo: calculoActual.periodo.diasBase,
                faltas: calculoActual.periodo.faltas,
                incapacidades: calculoActual.periodo.incapacidades,
                diasTrabajados: calculoActual.diasTrabajados,
                salarioBruto: calculoActual.empleado.salarioBruto,
                sbc: calculoActual.sbc,
                salarioDiario: calculoActual.salarioDiario,
                percepcionesDesglose: calculoActual.percepcionesDesglose || {},
                totalPercepciones: calculoActual.totalPercepciones,
                deducciones: calculoActual.deducciones,
                totalDeduccionesVal: calculoActual.totalDeduccionesVal || 0,
                netoPagar: calculoActual.netoPagar,
                transferenciaBancaria: calculoActual.transferenciaBancaria,
                estatus: 'Pendiente Aprobacion', // Flujo: Pendiente Aprobacion -> Aprobado -> Timbrado -> Pagado
                creadoPor: currentUser.uid
            };

            await setDoc(nominaRef, nominaPayload);

            // 2. Fetch Approval Rules for Payroll (Bypass Thresholds)
            const rules = await getApprovalRules(calculoActual.empleado.condominioId);
            const validation = validateInvoiceRules(calculoActual.netoPagar, rules, 0, true);

            // 3. Enviar a Gasto por Aprobar en Finanzas para que el Comité lo vea
            const gastoAprobacionRef = doc(collection(db, 'gastos'));
            const gastoPayload = {
                condominioId: calculoActual.empleado.condominioId,
                fecha: new Date(), // REQUIRED for orderBy("fecha", "desc") in Facturas dashboard
                fechaRegistro: new Date(),
                proveedorId: 'NOMINA_INTERNA',
                proveedorNombre: `Nómina: ${calculoActual.empleado.nombre} ${calculoActual.empleado.apellidos}`,
                categoria: 'Sueldos y Salarios',
                descripcion: `Recibo de Nómina Periodo (${calculoActual.periodo.diasBase} días)`,
                subtotal: calculoActual.totalPercepciones,
                iva: 0,
                retenciones_isr: calculoActual.deducciones.isr,
                retenciones_imss: calculoActual.deducciones.imss,
                total: calculoActual.netoPagar,
                estatus: validation.nextStatus || 'por_aprobar', // Required for visibility in Facturas Screen list
                estatusAprobacion: validation.status === 'approved' ? 'Aprobado' : 'Pendiente',
                requiresApproval: validation.requiresApproval,
                approvalLevel: validation.approvalLevel,
                approvers: validation.approvers,
                nominaReferenceId: reciboId // Link crudo para navegar al detalle
            };

            await setDoc(gastoAprobacionRef, gastoPayload);

            // 4. Marcar Incidencias como Aplicadas
            if (incidenciasPendientes.length > 0) {
                const arrPromises = incidenciasPendientes.map(inc => {
                    return setDoc(
                        doc(db, `empleados/${calculoActual.empleado.id}/incidencias`, inc.id),
                        { estatus: 'Aplicada', nominaReferenceId: reciboId },
                        { merge: true }
                    );
                });
                await Promise.all(arrPromises);
            }

            alert(isSaaS ? "Nómina guardada exitosamente en Módulo SaaS (Pendiente Dispersión)." : "Nómina guardada exitosamente y enviada a Comité de Aprobación.");
            setCalculoActual(null); // Reset
            setIncidenciasPendientes([]);
        } catch (error) {
            console.error("Error al guardar recibo:", error);
            alert("Ocurrió un error al intentar congelar el recibo.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute={isSaaS ? "/superuser/hr/gestion-nomina" : "/gestion-nomina"} />
            <main className="ks-main">
                <SafeAreaInsets top={false} bottom={true} />
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="gestion-nomina-container" style={{ padding: '20px', paddingTop: '0px', maxWidth: '100%', minHeight: 'auto', background: 'transparent' }}>
                        <div className="usuarios-fixed-wrapper" style={{ position: 'relative', margin: '0 0 20px 0', padding: 0, borderBottom: 'none', background: 'transparent' }}>
                            <div className="usuarios-banner-compact" style={{ background: isSaaS ? 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)' : 'linear-gradient(135deg, #10b981 0%, #047857 100%)' }}>
                                <button 
                                    onClick={() => navigate(isSaaS ? '/superuser/hr/dashboard' : '/nominas-dashboard')} 
                                    className="usuarios-back-btn glass"
                                    style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto', cursor: 'pointer' }}
                                >
                                    <IoArrowBack size={20} />
                                </button>
                                <div className="usuarios-banner-icon">
                                    <IoCashOutline size={120} opacity={0.1} />
                                </div>
                                <div className="usuarios-banner-content">
                                    <h2>{isSaaS ? 'SaaS Motor de Nómina' : 'Motor de Nómina'}</h2>
                                    <p>Simulador y Cálculo de Impuestos LFT / IMSS / SAT</p>
                                </div>
                            </div>
                        </div>

                        <div className="nomina-layout">
                            {/* SIDEBAR CONFIGURADOR */}
                            <div className="nomina-sidebar">
                                <div className="glass-card">
                                    <h3>1. Parámetros del Recibo</h3>

                                    <div className="form-group">
                                        <label>Empleado</label>
                                        <select
                                            value={selectedEmpleadoId}
                                            onChange={e => setSelectedEmpleadoId(e.target.value)}
                                            disabled={isReadOnly}
                                        >
                                            <option value="">Seleccione un Empleado...</option>
                                            {empleados.map(emp => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.nombre} {emp.apellidos} - {emp.puesto}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {incidenciasPendientes.length > 0 && !isReadOnly && (
                                        <div className="alert alert-warning" style={{ marginBottom: '1rem', fontSize: '0.85rem', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '8px', color: '#92400e', border: '1px solid #fcd34d' }}>
                                            <p style={{ margin: 0 }}><strong>¡Atención!</strong> Este empleado tiene <strong>{incidenciasPendientes.length} incidencia(s)</strong> sin aplicar en su expediente. Hemos auto-llenado los campos de abajo considerándolas.</p>
                                        </div>
                                    )}

                                    <div className="form-group" style={{ display: 'flex', gap: '10px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label>Del</label>
                                            <input type="date" value={fechaInicioPeriodo} onChange={e => setFechaInicioPeriodo(e.target.value)} disabled={isReadOnly} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label>Al</label>
                                            <input type="date" value={fechaFinPeriodo} onChange={e => setFechaFinPeriodo(e.target.value)} disabled={isReadOnly} />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Días del Periodo Pagado</label>
                                        <select value={diasPeriodo} onChange={e => setDiasPeriodo(e.target.value)} disabled={isReadOnly}>
                                            <option value="15">Quincenal (15 días)</option>
                                            <option value="7">Semanal (7 días)</option>
                                            <option value="14">Catorcenal (14 días)</option>
                                            <option value="30.4">Mensual (30.4 días)</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Faltas Injustificadas (Días)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={faltas}
                                            onChange={e => setFaltas(e.target.value)}
                                            disabled={isReadOnly}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Incapacidades IMSS (Días)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={incapacidades}
                                            onChange={e => setIncapacidades(e.target.value)}
                                            disabled={isReadOnly}
                                        />
                                    </div>

                                    {!isReadOnly && (
                                        <button
                                            className="btn-calcular"
                                            onClick={handleCalcular}
                                            disabled={!selectedEmpleadoId || loadingPrestaciones}
                                        >
                                            <IoCalculatorOutline size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                            {loadingPrestaciones ? 'Cargando datos...' : 'Calcular Recibo'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* VISUALIZADOR DE RECIBO */}
                            <div className="nomina-main-view">
                                <div className="glass-card" style={{ flex: 1 }}>
                                    <h3>2. Desglose de Cálculo (Fase Pre-Nómina)</h3>

                                    {!calculoActual ? (
                                        <div className="empty-state">
                                            <IoCashOutline size={64} />
                                            <p>Seleccione un empleado y oprima "Calcular Recibo" para ver el desglose matemático de impuestos y salarios.</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="nomina-header-info">
                                                <div className="info-item">
                                                    <span>Salario Bruto (Mensual)</span>
                                                    <span>{formatter.format(calculoActual.empleado.salarioBruto)}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span>Salario Diario Integrado (SBC)</span>
                                                    <span>{formatter.format(calculoActual.sbc)}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span>Días Trabajados</span>
                                                    <span>{calculoActual.diasTrabajados} de {calculoActual.periodo.diasBase}</span>
                                                </div>
                                                {(fechaInicioPeriodo || fechaFinPeriodo) && (
                                                    <div className="info-item" style={{ gridColumn: '1 / -1', background: '#e0e7ff', color: '#3730a3' }}>
                                                        <span>Periodo Pagado Descriptivo</span>
                                                        <span>{fechaInicioPeriodo ? fechaInicioPeriodo : 'N/A'} al {fechaFinPeriodo ? fechaFinPeriodo : 'N/A'}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="nomina-desglose">
                                                <div className="col-percepciones">
                                                    <h4>Percepciones (Ingresos)</h4>
                                                    <div className="row-item">
                                                        <span>Sueldo Ordinario ({calculoActual.diasTrabajados} días)</span>
                                                        <span>{formatter.format(calculoActual.percepcionesDesglose?.sueldo || calculoActual.totalPercepciones)}</span>
                                                    </div>
                                                    {calculoActual.percepcionesDesglose?.valesDespensa > 0 && (
                                                        <div className="row-item">
                                                            <span>Vales de Despensa (Especie)</span>
                                                            <span>{formatter.format(calculoActual.percepcionesDesglose.valesDespensa)}</span>
                                                        </div>
                                                    )}
                                                    {calculoActual.percepcionesDesglose?.fondoAhorroAportPatron > 0 && (
                                                        <div className="row-item">
                                                            <span>Aportación Patronal Fondo Ahorro</span>
                                                            <span>{formatter.format(calculoActual.percepcionesDesglose.fondoAhorroAportPatron)}</span>
                                                        </div>
                                                    )}
                                                    <div className="row-item total">
                                                        <span>Total Percepciones</span>
                                                        <span style={{ color: '#059669' }}>{formatter.format(calculoActual.totalPercepciones)}</span>
                                                    </div>
                                                </div>

                                                <div className="col-deducciones">
                                                    <h4>Deducciones (Retenciones)</h4>
                                                    <div className="row-item">
                                                        <span>ISR (Art. 96 Tablas SAT)</span>
                                                        <span>{formatter.format(calculoActual.deducciones.isr)}</span>
                                                    </div>
                                                    <div className="row-item">
                                                        <span>IMSS (Cuota Obrera)</span>
                                                        <span>{formatter.format(calculoActual.deducciones.imss)}</span>
                                                    </div>
                                                    {calculoActual.deducciones.faltasMonto > 0 && (
                                                        <div className="row-item">
                                                            <span>Faltas ({calculoActual.periodo.faltas} días)</span>
                                                            <span>{formatter.format(calculoActual.deducciones.faltasMonto)}</span>
                                                        </div>
                                                    )}
                                                    {calculoActual.deducciones.infonavit > 0 && (
                                                        <div className="row-item">
                                                            <span>Crédito INFONAVIT</span>
                                                            <span>{formatter.format(calculoActual.deducciones.infonavit)}</span>
                                                        </div>
                                                    )}
                                                    {calculoActual.deducciones.prestamos > 0 && (
                                                        <div className="row-item">
                                                            <span>Préstamo Empresarial</span>
                                                            <span>{formatter.format(calculoActual.deducciones.prestamos)}</span>
                                                        </div>
                                                    )}
                                                    {calculoActual.deducciones.fondoAhorroRetencion > 0 && (
                                                        <div className="row-item">
                                                            <span>Retención a Cta. Fondo Ahorro</span>
                                                            <span>{formatter.format(calculoActual.deducciones.fondoAhorroRetencion)}</span>
                                                        </div>
                                                    )}
                                                    <div className="row-item total">
                                                        <span>Total Deducciones</span>
                                                        <span style={{ color: '#dc2626' }}>
                                                            {formatter.format(calculoActual.totalDeduccionesVal || (
                                                                calculoActual.deducciones.isr +
                                                                calculoActual.deducciones.imss +
                                                                (calculoActual.deducciones.faltasMonto || 0) +
                                                                (calculoActual.deducciones.infonavit || 0) +
                                                                (calculoActual.deducciones.prestamos || 0)
                                                            ))}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="neto-pagar-box" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: calculoActual.percepcionesDesglose?.valesDespensa > 0 ? '0.5rem' : '0' }}>
                                                    <div style={{ fontSize: '1.2rem'}}>{calculoActual.percepcionesDesglose?.valesDespensa > 0 ? "Efectivo a Transferir (Banco)" : "Neto a Pagar al Empleado"}</div>
                                                    <div>{formatter.format(calculoActual.transferenciaBancaria || calculoActual.netoPagar)}</div>
                                                </div>
                                                
                                                {calculoActual.percepcionesDesglose?.valesDespensa > 0 && (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1rem', borderBottom: '1px solid #334155', paddingBottom: '1rem' }}>
                                                            <span>Abono a Monedero de Vales</span>
                                                            <span>{formatter.format(calculoActual.percepcionesDesglose.valesDespensa)}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem' }}>
                                                            <span style={{ color: '#cbd5e1' }}>Total Remuneración Neta</span>
                                                            <span style={{ color: '#10b981' }}>{formatter.format(calculoActual.netoPagar)}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {!isReadOnly && (
                                                <button
                                                    className="btn-aprobar"
                                                    onClick={handleGuardarRecibo}
                                                    disabled={isSaving}
                                                >
                                                    <IoCheckmarkCircleOutline size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                                    {isSaving ? 'Guardando en BD...' : 'Guardar y Congelar Recibo'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <KondoBottomNav activeRoute={isSaaS ? "/superuser/hr/gestion-nomina" : "/gestion-nomina"} />
            </main>
        </div>
    );
}

export default GestionNominaScreen;
